import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';


const EarningsChart = ({ userType, earnings = [] }) => {
  const [timeRange, setTimeRange] = useState('month');

  // Transform earnings data for chart
  const monthlyData = [
    { month: 'Jan', earnings: 0, spending: 0 },
    { month: 'Feb', earnings: 0, spending: 0 },
    { month: 'Mar', earnings: 0, spending: 0 },
    { month: 'Apr', earnings: 0, spending: 0 },
    { month: 'May', earnings: 0, spending: 0 },
    { month: 'Jun', earnings: 0, spending: 0 }
  ];

  // Populate with real earnings data
  earnings?.forEach(earning => {
    const monthIndex = (earning?.periodMonth || 1) - 1;
    if (monthIndex >= 0 && monthIndex < 6) {
      if (userType === 'provider') {
        monthlyData[monthIndex].earnings += parseFloat(earning?.amount || 0);
      } else {
        monthlyData[monthIndex].spending += parseFloat(earning?.amount || 0);
      }
    }
  });

  const weeklyData = [
    { week: 'Week 1', earnings: 0, spending: 0 },
    { week: 'Week 2', earnings: 0, spending: 0 },
    { week: 'Week 3', earnings: 0, spending: 0 },
    { week: 'Week 4', earnings: 0, spending: 0 }
  ];

  const data = timeRange === 'month' ? monthlyData : weeklyData;
  const dataKey = userType === 'provider' ? 'earnings' : 'spending';
  const label = userType === 'provider' ? 'Earnings' : 'Spending';

  const chartData = data.map(item => ({
    date: item.month || item.week,
    amount: item[dataKey]
  }));

  const totalEarnings = chartData.reduce((sum, item) => sum + item.amount, 0);
  const avgEarnings = chartData.length > 0 ? totalEarnings / chartData.length : 0;
  const maxEarnings = chartData.length > 0 ? Math.max(...chartData.map(item => item.amount)) : 0;

  return (
    <div className="bg-card rounded-md p-3 md:p-6 shadow-warm">
      <div className="flex items-center justify-between mb-3 md:mb-6">
        <h2 className="text-base md:text-xl font-heading font-semibold text-foreground">
          {userType === 'provider' ? 'Earnings' : 'Spending'} Overview
        </h2>
        <select className="px-2 py-1 md:px-3 md:py-1.5 rounded-md border border-border text-xs md:text-sm bg-background text-foreground">
          <option>Last 7 days</option>
          <option>Last 30 days</option>
          <option>Last 3 months</option>
        </select>
      </div>
      <div className="h-48 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              stroke="hsl(var(--muted-foreground))" 
              fontSize={10}
              className="md:text-xs"
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))" 
              fontSize={10}
              className="md:text-xs"
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.375rem',
                fontSize: '12px'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="amount" 
              stroke="hsl(var(--primary))" 
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--primary))', r: 3 }}
              className="md:stroke-[3]"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-3 gap-2 md:gap-4 mt-3 md:mt-6">
        <div className="text-center p-2 md:p-3 bg-muted rounded-md">
          <p className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Total</p>
          <p className="text-sm md:text-lg font-bold text-foreground">₱{totalEarnings?.toLocaleString()}</p>
        </div>
        <div className="text-center p-2 md:p-3 bg-muted rounded-md">
          <p className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Average</p>
          <p className="text-sm md:text-lg font-bold text-foreground">₱{avgEarnings?.toLocaleString()}</p>
        </div>
        <div className="text-center p-2 md:p-3 bg-muted rounded-md">
          <p className="text-[10px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Highest</p>
          <p className="text-sm md:text-lg font-bold text-foreground">₱{maxEarnings?.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default EarningsChart;